import React, { useEffect, useRef, useState, useCallback } from "react";
import { BarcodeDetector } from "barcode-detector";
import { detectBarcodes } from "../lib/barcodes";
import ScannerUI from "./ScannerUI";

export default function BarcodeScanner({
  formatsToSupport = ["pdf417"],
  tipText = "",
  onResult,
  onClose,
}) {
  // refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const abortControllerRef = useRef(new AbortController());

  // state
  const [running, setRunning] = useState(false);
  const [cameraList, setCameraList] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const [cameraTorch, setCameraTorch] = useState(false);
  const [loadingCamera, setLoadingCamera] = useState(true);
  const [cameraConstraints, setCameraConstraints] = useState({
    zoom: { min: 1, max: 8, step: 1 },
  });
  const [capabilities, setCapabilities] = useState({
    zoom: false,
    torch: false,
  });

  // -----------------------------
  // Camera Logic
  // -----------------------------
  const stopCamera = useCallback(() => {
    abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setRunning(false);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startVideoStream = async ({ constraints } = {}) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: "environment",
        width: { ideal: 1280 },
        height: { ideal: 720 },
        ...constraints?.video,
      },
    });

    streamRef.current = stream;

    const videoEl = videoRef.current;

    videoRef.current = videoEl;
    videoEl.srcObject = stream;

    await new Promise((res) => {
      videoEl.onloadedmetadata = () => res();
    });
  };

  const getCapabilities = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    const caps = track.getCapabilities();
    const { zoom, torch } = caps;

    setCapabilities({
      zoom: !!zoom,
      torch: !!torch,
    });

    if (zoom) {
      const { max = 8, min = 1, step = 1 } = zoom;
      setCameraConstraints({
        zoom: { min, max, step },
      });
    }
  };

  const loadCameraList = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setCameraList(devices.filter((d) => d.kind === "videoinput"));
    } catch (err) {
      setCameraList([]);
    }
  };

  const emitScanResult = async ({ barcodeDetector, video }) => {
    const { signal } = abortControllerRef.current;

    try {
      const barcodes = await detectBarcodes({ barcodeDetector, video, signal });
      const [result] = barcodes;

      onResult?.({ type: result.format, text: result.rawValue });
    } catch (e) {
      if (e.name !== "AbortError") {
        onClose?.();
      }
    }
  };

  const startBarcodeDetection = () => {
    const detector = new BarcodeDetector({ formats: formatsToSupport });
    emitScanResult({ barcodeDetector: detector, video: videoRef.current });
  };

  const startCamera = useCallback(
    async ({ constraints } = {}) => {
      if (running) stopCamera();

      setLoadingCamera(true);
      setCameraError(false);
      setRunning(true);

      try {
        await startVideoStream({ constraints });
        getCapabilities();
        await loadCameraList();
        startBarcodeDetection();
      } catch (err) {
        setRunning(false);
        setCameraError(true);
      } finally {
        setLoadingCamera(false);
      }
    },
    [running]
  );

  // -----------------------------
  // Lifecycle
  // -----------------------------
  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  // -----------------------------
  // Helpers
  // -----------------------------
  const handleClose = () => {
    stopCamera();
    onClose?.();
  };

  const toggleTorch = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    const newTorch = !cameraTorch;
    setCameraTorch(newTorch);

    track.applyConstraints({ advanced: [{ torch: newTorch }] }).catch(() => {
      // Silently handle torch constraint error
    });
  };

  const onZoomChange = async (value) => {
    const stream = streamRef.current;
    if (!stream) return;

    const track = stream.getVideoTracks()[0];
    track.applyConstraints({ advanced: [{ zoom: value }] }).catch(() => {
      // Silently handle zoom constraint error
    });
  };

  const onCameraChange = async (deviceId) => {
    const constraints = {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    };
    await startCamera({ constraints });
  };

  // -----------------------------
  // JSX Render
  // -----------------------------
  return (
    <ScannerUI
      tipText={tipText}
      cameraList={cameraList}
      cameraError={cameraError}
      capabilities={capabilities}
      loadingCamera={loadingCamera}
      cameraConstraints={cameraConstraints}
      onClose={handleClose}
      onToggleTorch={toggleTorch}
      onUpdateCamera={onCameraChange}
      onZoomUpdate={onZoomChange}
      videoRef={videoRef}
    />
  );
}
